import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { useTranslation } from "@privacy/translations";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Drawer } from "../shared/Drawer";
import { DynamicForm } from "../dynamicForm";
import { DynamicGrid } from "../dynamicGrid";
import { usePermission } from "../shared/permissions";
import { TenantIcon2 } from "../shared/icons";
import { usePrivacyNavigate } from "../shared/usePrivacyNavigate";
import { prefetch } from "../api/prefetch";
import {
  setSharedProcessingResponsibleMutationOptions,
  sharedProcessingByIdQueryOptions,
  sharedProcessingFormQueryOptions,
  sharedProcessingSharedOrganisationsQueryOptions,
  updateSharedProcessingAnswersMutationOptions,
} from "../api/sharedProcessings";
import { DateTimeInfo } from "../pages/RegistrationsGridPage/RegistrationDrawer/DateTimeInfo";
import { SharedProcessingDrawerHeader } from "./SharedProcessingDrawerHeader";
import { SharedProcessingResponsible } from "./SharedProcessingResponsible";

export const SharedProcessingInlineFormDrawer = () => {
  const sharedProcessingId = DynamicGrid.useSelectedRowId();
  const { t } = useTranslation();
  const sharedProcessingQuery = useQuery(sharedProcessingByIdQueryOptions(sharedProcessingId));
  const formQuery = useQuery(sharedProcessingFormQueryOptions());
  const answersMutation = useMutation(updateSharedProcessingAnswersMutationOptions());
  const setResponsibleMutation = useMutation(setSharedProcessingResponsibleMutationOptions());
  const { hasPermission } = usePermission();
  const { navigate } = usePrivacyNavigate();
  const isReadOnly = !hasPermission("nova-forms-api:enterprise-customers:registrations:manage");

  return (
    <DynamicGrid.Drawer>
      {(() => {
        if (!sharedProcessingQuery.data || !formQuery.data) return <Drawer.Loading />;

        const sharedProcessing = sharedProcessingQuery.data;
        return (
          <DynamicForm.Provider
            key={sharedProcessing.id}
            fileOwner={{ type: "Registration", id: sharedProcessing.id }}
            onSave={answer => answersMutation.mutateAsync({ id: sharedProcessing.id, answers: [answer] })}
            answers={sharedProcessing.answers}
            isReadOnly={isReadOnly}
            isSaveError={answersMutation.isError || setResponsibleMutation.isError}
            isSaving={answersMutation.isPending || setResponsibleMutation.isPending}
          >
            {({ formState }) => (
              <>
                <SharedProcessingDrawerHeader
                  activeTab="standard"
                  sharedProcessing={sharedProcessing}
                  statusIndicator={DynamicForm.formStateToDrawerHeaderStatus(formState)}
                  bottomSlot={
                    <Box>
                      <Divider />
                      <Stack sx={{ pb: 2, pt: 3, px: Drawer.PADDING_X, gap: 2 }}>
                        <Stack spacing={0.5}>
                          <DateTimeInfo
                            label={t("RegistrationGrid.CreatedAtHeader")}
                            datetime={sharedProcessing.createdAt}
                          />
                          <DateTimeInfo
                            label={t("RegistrationGrid.UpdatedAtHeader")}
                            datetime={sharedProcessing.updatedAt}
                          />
                        </Stack>
                      </Stack>
                    </Box>
                  }
                />
                <Drawer.Content sx={{ pt: 3 }}>
                  <Stack sx={{ mb: 5 }}>
                    <SharedProcessingResponsible
                      isReadOnly={isReadOnly}
                      sharedProcessing={sharedProcessing}
                      setResponsibleMutation={setResponsibleMutation}
                    />
                    {!isReadOnly && (
                      <Drawer.GoToSubDrawerButtonsWrapper>
                        <Drawer.GoToSubDrawerButton
                          Icon={TenantIcon2}
                          label={t("SharedProcessing.Share.CardLabel")}
                          onClick={() => navigate("share")}
                          onMouseOver={() =>
                            prefetch(sharedProcessingSharedOrganisationsQueryOptions(sharedProcessing.id))
                          }
                        />
                      </Drawer.GoToSubDrawerButtonsWrapper>
                    )}
                  </Stack>

                  <Drawer.Divider />
                  <Stack sx={{ my: 5 }}>
                    <DynamicForm.InlineForm id={sharedProcessing.id} form={formQuery.data} />
                  </Stack>
                </Drawer.Content>
              </>
            )}
          </DynamicForm.Provider>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};
